/**
 * contractStats.js — 수주 통계 집계 (순수 함수).
 *
 * 입력: contract list (array). 각 항목은 다음 필드를 가질 수 있다.
 *   - contractDt: 수주일 (string, YYYY-MM-DD 또는 ISO)
 *   - partnerName: 거래처명 (string)
 *   - dueDt: 납기일 (string)
 *   - releaseDt: 출고일 (string | null)
 *
 * 출력: {
 *   totalCount: number,
 *   daily: [{ date: 'YYYY-MM-DD', count: number }] (오름차순),
 *   partnerTop5: [{ label, value }],
 *   statusDistribution: [
 *     { label: '정시출고', value },
 *     { label: '지연출고', value },
 *     { label: '미출고', value },
 *   ],
 *   ontimeRate: number | null,
 * }
 */

const STATUS_LABELS = ['정시출고', '지연출고', '미출고'];

const toDateOnly = (value) => {
  if (!value) return '';
  const s = String(value);
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(s);
  return m ? m[1] : '';
};

const compareDateStr = (a, b) => {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
};

const classifyStatus = (item) => {
  const release = toDateOnly(item.releaseDt);
  if (!release) return '미출고';
  const due = toDateOnly(item.dueDt);
  if (!due) return '정시출고';
  return compareDateStr(release, due) <= 0 ? '정시출고' : '지연출고';
};

export function buildContractStats(list) {
  const safeList = Array.isArray(list) ? list : [];

  const totalCount = safeList.length;

  const dailyMap = new Map();
  safeList.forEach((item) => {
    const date = toDateOnly(item.contractDt);
    if (!date) return;
    dailyMap.set(date, (dailyMap.get(date) || 0) + 1);
  });
  const daily = Array.from(dailyMap.entries())
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => compareDateStr(a.date, b.date));

  const partnerMap = new Map();
  safeList.forEach((item) => {
    const name = item.partnerName || '(미지정)';
    partnerMap.set(name, (partnerMap.get(name) || 0) + 1);
  });
  const partnerSorted = Array.from(partnerMap.entries())
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);
  const partnerTop5 = partnerSorted.slice(0, 5);

  const counts = { 정시출고: 0, 지연출고: 0, 미출고: 0 };
  safeList.forEach((item) => {
    const s = classifyStatus(item);
    counts[s] = (counts[s] || 0) + 1;
  });
  const statusDistribution = STATUS_LABELS.map((label) => ({
    label,
    value: counts[label] || 0,
  }));

  const released = counts['정시출고'] + counts['지연출고'];
  const ontimeRate =
    released === 0 ? null : Math.round((counts['정시출고'] / released) * 1000) / 10;

  return {
    totalCount,
    daily,
    partnerTop5,
    statusDistribution,
    ontimeRate,
  };
}

export default buildContractStats;
